/* global I18n */

import preload_image from "preload_image"
import {daltonize_image} from "daltonize_images"
import React, {useState, useEffect} from "react"
import $ from "cash-dom"

// shows a warning when leaving the page
export const use_leave_site_alert = (initial_leave_site_alert) => {
  const [leave_site_alert, set_leave_site_alert] =
    useState(initial_leave_site_alert)
  useEffect(() => {
    window.onbeforeunload = (e) => {
      if (!leave_site_alert) return
      e = e || window.event
      // Chrome requires returnValue to be set
      return (e.returnValue = "")
    }
    const show_confirmation = () => {
      if (leave_site_alert) return confirm(I18n.t("js.leave_site_alert"))
    }
    $(document).on("turbo:before-visit", show_confirmation)
    return () => {
      $(document).off("turbo:before-visit", show_confirmation)
      window.onbeforeunload = undefined
    }
  }, [leave_site_alert])
  return set_leave_site_alert
}

// used to make the Algolia search results infinite scrollable
export const use_infinite_scroll = (isLastPage, showMore) => {
  const [last_element, set_last_element] = useState(null)
  const on_last_element_intersection = entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !isLastPage) showMore()
    })
  }
  useEffect(() => {
    if (!last_element) return
    const observer = new IntersectionObserver(on_last_element_intersection)
    observer.observe(last_element)
    return () => observer.disconnect()
  }, [last_element, showMore])
  return set_last_element
}

// listends to any click which was done outside the popover
// and calls the on_close callback, except when the click was done on target
// reference_element: dom element which should be ignored by the click listener
// including its children
export const use_outside_click_listener = (
  reference_element,
  callback,
  options
) =>
  React.useEffect(() => {
    if (!callback) return
    if (!reference_element) return
    const click_callback = (e) =>
      on_click_callback(e, reference_element, callback, options)
    // we can't use document.addEventListener here because this listener
    // wouldn't be cleaned up when going to another page with turbo
    $(document).on("mousedown", click_callback)
    return () => $(document).off("mousedown", click_callback)
  })

const on_click_callback = (e, reference_element, callback, options) => {
  if (!reference_element) return
  if (check_exception(e, options)) return
  if (reference_element.contains(e.target)) return
  return callback()
}

// check if the exception element was clicked or any element inside of it
const check_exception = (e, options) => {
  if (!options.except) return
  return is_element_or_parent(e.target, options.except)
}

// inspired by
// https://developer.mozilla.org/en-US/docs/Web/API/Element/closest#Polyfill
const is_element_or_parent = (current_el, parent_el) => {
  while (current_el?.nodeType === 1) {
    if (current_el === parent_el) return true
    current_el = current_el.parentElement || current_el.parentNode
  }
  return false
}

export const use_preload_image = (image_url) => {
  const [current_image_url, set_current_image_url] = useState(null)
  React.useEffect(() => {
    let is_mounted = true
    preload_image(image_url).then((new_url) => {
      if (is_mounted) set_current_image_url(new_url)
    })
    return () => {
      is_mounted = false
    }
  }, [image_url])
  return current_image_url
}

const cache = {}

// based on https://github.com/mbrevda/react-image/blob/d127cfe469e41c1f8658e57d2638db4c452a90d3/src/useImage.tsx
// the component which uses this hook needs to be wrapped by
// <Suspense fallback={} ></Suspense> to provide a loading component
export const use_daltonize_image = (image_url) => {
  if (window.gon.color_blindness === "normal") return image_url

  if (!cache[image_url]) {
    cache[image_url] = {
      promise: daltonize_image(image_url),
      state: "pending",
      error: null
    }
  }
  cache[image_url].promise
    .then((src) => {
      cache[image_url] = {...cache[image_url], state: "resolved", src}
    })
    .catch((error) => {
      cache[image_url] = {...cache[image_url], state: "rejected", error}
    })

  if (cache[image_url].state === "resolved") {
    return cache[image_url].src
  }

  if (cache[image_url].state === "rejected") {
    throw cache[image_url].error
  }
  // cache[image_url].state === 'pending')
  throw cache[image_url].promise
}

export const use_on_hover_event = (target, on_enter, on_leave) => {
  useEffect(() => {
    if (!target) return

    for (const event of ["mouseenter", "focus"]) {
      target.addEventListener(event, on_enter)
    }

    for (const event of ["mouseleave", "blur"]) {
      target.addEventListener(event, on_leave)
    }
  }, [target, on_enter, on_leave])
}
